import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createUserWithEmailAndPassword, OAuthProvider, signOut } from 'firebase/auth'
import { RootState } from '../../app/store'

import axios from 'axios'
import { getAnalytics } from 'firebase/analytics'
import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth"
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'
import { API_BASE, IS_LITE, LITE_TOKEN } from '../../app/const'

export interface Authority {
  authority: string
}
export interface UserRole {
  id: number
  role?: string
  userId: string
  userEmail?: string
  paidTier: number
  paidTierCount: number
  paidTierExpiry: number
  org: Org
}

export interface InvitedUsers {
  id: number
  orgRole?: string
  userId?: string
  userEmail?: string
  invitedBy?: string
  createdTime?: string
  org?: Org
}

export interface Org {
  id: number
  name: string

  ownerId: string
  ownerEmail: string
  paidTier: number
  paidTierCount: number
  paidTierExpiry: number
}
export interface AppUser {
  uid: string
  displayName: string | null
  email: string | null
  photoURL: string | null
  providerId: string
  emailVerified: boolean
  authorities: [Authority]
  paidTier: number
  paidTierCount: number
  paidTierExpiry: number
  org?: Org
  orgPaidTierCount: number
  createdTime?: string
}
export interface AppCredential {
  uid: string
  ghuser: string
  ghid: string
  idToken?: string
  accessToken?: string
  providerId: string
  secret?: string
  signinMethod: string
}
export interface AuthState {
  isAuthenticated: boolean
  isMaintenance: boolean
  maintenanceMessage?: string
  futureMaintenanceMessage?: string
  showCustomizePreview?: boolean
  pdfApiVersion?: string
  status: 'idle' | 'loading' | 'failed'
  initStatus: 'idle' | 'loading' | 'failed'
  user?: AppUser,
  credential?: AppCredential
  fault?: any
  sendResetPasswordEmail?: boolean
  jwt?: string
}

const initialState: AuthState = {
  isAuthenticated: false,
  isMaintenance: false,
  status: 'idle',
  initStatus: 'loading',
}

// Initialize Firebase // "https://console.firebase.google.com/project/rent-iq/settings/general/web"
export const fireapp = initializeApp({
  apiKey: "AIzaSyDihThztOq5eG1mCnyb6ujBUcbnYnbni4E",
  authDomain: "rent-iq.firebaseapp.com",
  projectId: "rent-iq",
  storageBucket: "rent-iq.appspot.com",
  messagingSenderId: "715662282933",
  appId: "1:715662282933:web:59db8674f8cac2f8d46efc",
  measurementId: "G-TJEFDDX026",
})
export const analytics = getAnalytics(fireapp)
export const fireauth = getAuth(fireapp)
export const remoteConfig = getRemoteConfig(fireapp)
//remoteConfig.settings.minimumFetchIntervalMillis = 43200000 // Default 43200000 (12 hours)
//remoteConfig.settings.fetchTimeoutMillis = 60000 // Default 60000 (1 minute)

// https://firebase.google.com/docs/auth/web/github-auth
const providerGoogle = new GoogleAuthProvider()
// https://docs.github.com/en/developers/apps/building-oauth-apps/scopes-for-oauth-apps#available-scopes
providerGoogle.addScope('openid, https://www.googleapis.com/auth/userinfo.email, https://www.googleapis.com/auth/userinfo.profile')

// https://firebase.google.com/docs/auth/web/microsoft-oauth
const providerMicrosoft = new OAuthProvider('microsoft.com')

// authInitAsync is deprecated
export const authInitAsync = createAsyncThunk('auth/init', async (args: any, { getState }) => new Promise<{}>((resolve) => setTimeout(() => resolve({}), 200)))

export const remoteConfigAsync = createAsyncThunk('auth/remoteConfigAsync', async (args: any, { getState }) => {
  const state = getState() as RootState
  if (!state.auth.isAuthenticated) {
    return fetchAndActivate(remoteConfig).then(() => {
      return {
        isMaintenance: getValue(remoteConfig, "isMaintenance").asBoolean(),
        maintenanceMessage: getValue(remoteConfig, "maintenanceMsg").asString(),
        futureMaintenanceMessage: getValue(remoteConfig, "futureMaintenanceMsg").asString(),
        showCustomizePreview: getValue(remoteConfig, "showCustomizePreview").asBoolean(),
        pdfApiVersion: getValue(remoteConfig, "pdfApiVersion").asString(),
      }
    }).catch((err) => {
      console.log('remoteConfig fetch failed', err)
      return { isMaintenance: false, maintenanceMessage: '', futureMaintenanceMessage: '', showCustomizePreview: false, pdfApiVersion: 'v1' }
    })
  }
})

export const signOutAsync = createAsyncThunk('auth/signOut', async (args: any, { getState }) => signOut(fireauth))

export const signInAsync = createAsyncThunk(
  'auth/signInWithPopup', async ({ redirectTo, providerId }: { redirectTo: string, providerId: 'Google' | 'Microsoft' }, { getState }) => {
    const state = getState() as RootState
    if (!state.auth.isAuthenticated) {
      providerGoogle.setCustomParameters({ redirectTo })
      providerMicrosoft.setCustomParameters({ redirectTo })
      const result = await signInWithPopup(fireauth, providerId === 'Google' ? providerGoogle : providerMicrosoft)
      const credential = providerId === 'Google' ? GoogleAuthProvider.credentialFromResult(result) : OAuthProvider.credentialFromResult(result)
      const userProfile = await axios.get(`${API_BASE}apps/user/profile`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
        }
      })
      return {
        user: {
          uid: result.user.uid,
          displayName: result.user.displayName,
          email: result.user.email,
          photoURL: result.user.photoURL,
          providerId: result.user.providerId,
          emailVerified: result.user.emailVerified,
          ...userProfile?.data // authorities, subscribedOn, expiresOn
        },
        ...credential && {
          credential: {
            uid: result.user.uid,
            providerId: credential.providerId,
            signinMethod: credential.signInMethod,
            idToken: credential.idToken,
            accessToken: credential.accessToken,
            secret: credential.secret,
          }
        }
      }
    }
  }
)

export const registerWithEmailAndPasswordAsync = createAsyncThunk(
  'auth/registerWithEmailAndPassword', async ({ redirectTo, email, password }: { redirectTo: string, email: string, password: string }, { getState }) => {
    const state = getState() as RootState
    if (!state.auth.isAuthenticated) {
      try {
        const result = await createUserWithEmailAndPassword(fireauth, email, password)
        console.log('createUserWithEmailAndPassword result', result)
        const userProfile = await axios.get(`${API_BASE}apps/user/profile`, {
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
            "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
          }
        })
        return {
          user: {
            uid: result.user.uid,
            displayName: result.user.displayName,
            email: result.user.email,
            photoURL: result.user.photoURL,
            providerId: result.user.providerId,
            emailVerified: result.user.emailVerified,
            ...userProfile?.data // authorities, subscribedOn, expiresOn
          },
          ...result && {
            credential: {
              uid: result.user.uid,
              providerId: result.providerId,
              signinMethod: 'emailAndPassword',
              idToken: result.user.getIdToken(),
              accessToken: undefined,
              secret: undefined
            }
          }
        }
      } catch (error) {
        console.log('createUserWithEmailAndPassword error', error)
        return { user: undefined, credential: undefined, error }
      }
    }
  }
)

export const signInWithEmailAndPasswordAsync = createAsyncThunk(
  'auth/signInWithEmailAndPassword', async ({ redirectTo, email, password }: { redirectTo: string, email: string, password: string }, { getState }) => {
    const state = getState() as RootState
    if (!state.auth.isAuthenticated) {
      try {
        const result = await signInWithEmailAndPassword(fireauth, email, password)
        const userProfile = await axios.get(`${API_BASE}apps/user/profile`, {
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
            "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
          }
        })
        return {
          user: {
            uid: result.user.uid,
            displayName: result.user.displayName,
            email: result.user.email,
            photoURL: result.user.photoURL,
            providerId: result.user.providerId,
            emailVerified: result.user.emailVerified,
            ...userProfile?.data // authorities, subscribedOn, expiresOn
          },
          ...result && {
            credential: {
              uid: result.user.uid,
              providerId: result.providerId,
              signinMethod: 'emailAndPassword',
              idToken: result.user.getIdToken(),
              accessToken: undefined,
              secret: undefined
            }
          }
        }
      } catch (error) {
        console.log('signInWithEmailAndPassword error', error)
        return { user: undefined, credential: undefined, error }
      }
    }
  }
)

export const sendPasswordResetEmailAsync = createAsyncThunk('auth/sendPasswordResetEmail', async (email: string, { getState }) => {
  const state = getState() as RootState
  if (!state.auth.isAuthenticated) {
    await sendPasswordResetEmail(fireauth, email)
  }
})


export const getUserRolesAsync = createAsyncThunk('auth/getUserRolesAsync', async (args: any, { getState }) => {
  const state = getState() as RootState
  if (state.auth.isAuthenticated) {
    const userProfile = await axios.get(`${API_BASE}apps/user/profile`, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${IS_LITE ? LITE_TOKEN : await fireauth.currentUser?.getIdToken()}`
      }
    })
    return userProfile?.data
  }
})

export const fetchTokenAsync = createAsyncThunk('auth/fetchToken', async () => {
  return await fireauth.currentUser?.getIdToken()
})

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authStateChange: (state, action: PayloadAction<{ user: AppUser }>) => {
      state.isAuthenticated = !!action.payload.user
      if (action.payload.user) {
        state.user = action.payload.user
      }
    },
    setAuthCredentials: (state, action: PayloadAction<AppCredential>) => {
      state.credential = action.payload
    },
    resetFault: (state) => {
      state.fault = undefined
    },
    resetSendResetPasswordEmail: (state) => {
      state.sendResetPasswordEmail = false
    },
  }, extraReducers: (builder) => {
    builder
      .addCase(signInAsync.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(signInAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        if (action.payload) {
          state.isAuthenticated = !!action.payload.user
          state.user = action.payload.user
          state.credential = action.payload.credential as any
        }
      })
      .addCase(signInAsync.rejected, (state) => {
        state.status = 'failed'
      }).addCase(registerWithEmailAndPasswordAsync.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(registerWithEmailAndPasswordAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        if (action.payload) {
          state.isAuthenticated = !!action.payload.user
          state.user = action.payload.user
          state.credential = action.payload.credential as any
          state.fault = action.payload.error
        }
      })
      .addCase(registerWithEmailAndPasswordAsync.rejected, (state) => {
        state.status = 'failed'
      }).addCase(signInWithEmailAndPasswordAsync.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(signInWithEmailAndPasswordAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        if (action.payload) {
          state.isAuthenticated = !!action.payload.user
          state.user = action.payload.user
          state.credential = action.payload.credential as any
          state.fault = action.payload.error
        }
      })
      .addCase(signInWithEmailAndPasswordAsync.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(authInitAsync.pending, (state, action) => {
        state.initStatus = 'loading'
      }).addCase(authInitAsync.fulfilled, (state, action) => {
        state.initStatus = 'idle'
      }).addCase(authInitAsync.rejected, (state, action) => {
        state.initStatus = 'failed'
      }).addCase(signOutAsync.pending, (state, action) => {
        state.status = 'loading'
      }).addCase(signOutAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.isAuthenticated = false
        state.credential = undefined
        state.user = undefined
      }).addCase(signOutAsync.rejected, (state, action) => {
        console.log('signout failed')
        state.status = 'failed'
        state.isAuthenticated = false
        state.credential = undefined
        state.user = undefined
      }).addCase(getUserRolesAsync.pending, (state, action) => {
        state.status = 'loading'
      }).addCase(getUserRolesAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        if (action.payload && state.user) {
          state.user.authorities = action.payload?.authorities
          state.user.paidTier = action.payload?.paidTier
          state.user.paidTierCount = action.payload?.paidTierCount
          state.user.paidTierExpiry = action.payload?.paidTierExpiry
          state.user.orgPaidTierCount = action.payload?.orgPaidTierCount
          state.user.org = action.payload?.org
          state.user.createdTime = action.payload?.createdTime
        }
      }).addCase(getUserRolesAsync.rejected, (state, action) => {
        state.status = 'failed'
      }).addCase(remoteConfigAsync.pending, (state, action) => {
        state.status = 'loading'
      }).addCase(remoteConfigAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        if (action.payload) {
          state.isMaintenance = action.payload.isMaintenance
          state.maintenanceMessage = action.payload.maintenanceMessage
          state.futureMaintenanceMessage = action.payload.futureMaintenanceMessage
          state.showCustomizePreview = action.payload.showCustomizePreview
          state.pdfApiVersion = action.payload.pdfApiVersion
        }
      }).addCase(remoteConfigAsync.rejected, (state, action) => {
        state.status = 'failed'
      }).addCase(sendPasswordResetEmailAsync.pending, (state, action) => {
        state.status = 'loading'
      }).addCase(sendPasswordResetEmailAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.sendResetPasswordEmail = true
      }).addCase(sendPasswordResetEmailAsync.rejected, (state, action) => {
        state.status = 'failed'
      }).addCase(fetchTokenAsync.fulfilled, (state, action) => {
        state.jwt = action.payload
      }).addCase(fetchTokenAsync.rejected, (state, action) => {
        state.jwt = undefined
      })
  },
})
export const authStatus = (state: RootState) => state.auth.status
export const selectAuthUser = (state: RootState) => state.auth.user
export const selectIsNotOrgUser = (state: RootState) => state.auth.user ? state.auth.user.authorities.every(authority => !['ROLE_ORG_OWNER', 'ROLE_ORG_ADMIN', 'ROLE_ORG_USER'].includes(authority.authority)) : false;
export const selectAuthFault = (state: RootState) => state.auth.fault
export const selectIsAuth = (state: RootState) => state.auth.isAuthenticated
export const selectAuthInitStatus = (state: RootState) => state.auth.initStatus
export const selectIsMaintenance = (state: RootState) => state.auth.isMaintenance
export const selectShowCustomizePreview = (state: RootState) => state.auth.showCustomizePreview
export const selectPdfApiVersion = (state: RootState) => state.auth.pdfApiVersion
export const selectMaintenanceMessage = (state: RootState) => state.auth.maintenanceMessage
export const selectFutureMaintenanceMessage = (state: RootState) => state.auth.futureMaintenanceMessage
export const selectSendResetPasswordEmail = (state: RootState) => state.auth.sendResetPasswordEmail
export const selectJwt = (state: RootState) => state.auth.jwt
export const { authStateChange, setAuthCredentials, resetFault, resetSendResetPasswordEmail } = authSlice.actions
export default authSlice.reducer
